import React, { createContext, ReactNode, useCallback, useMemo, useState, MouseEvent } from "react";
import { CareRecipient } from "../../domain/care_recipient/CareRecipient";
import { Paths } from "../../Paths";
import { useAppDispatch } from "../../state/store";
import { selectCareRecipientAction } from "../../state/usecase/care_recipient/actions";
import { useCareRecipientList } from "../hooks/care_recipient/useCareRecipientList";
import { useNavigate } from "react-router-dom";

type ContextType = {
    countPerPage: number
    currentPage: number
    setCurrentPage: React.Dispatch<React.SetStateAction<number>>
    searchText: string
    setSearchText: React.Dispatch<React.SetStateAction<string>>
    searchedList: CareRecipient[]
    displayList: CareRecipient[]
    handleViewReportClicked: (careRecipient: CareRecipient) => void
    handleConfigurationClicked: (event: MouseEvent, careRecipient: CareRecipient) => void
};

const countPerPage = 5;

const defaultContextValue: ContextType = {
    countPerPage: countPerPage,
    currentPage: 0,
    setCurrentPage: () => {},
    searchText: "",
    setSearchText: () => {},
    searchedList: [],
    displayList: [],
    handleViewReportClicked: (_careRecipient: CareRecipient) => {},
    handleConfigurationClicked: (_event: MouseEvent, _careRecipient: CareRecipient) => {}
};

export const CareRecipientListContext = createContext(defaultContextValue);

export const CareRecipientListProvider = React.memo(function CareRecipientListProvider(props: Props) {
    const dispatch = useAppDispatch();

    const navigate = useNavigate();

    const { careRecipients } = useCareRecipientList();

    const [currentPage, setCurrentPage] = useState(0);

    const [searchText, setSearchText] = useState("");

    const searchedList = useMemo(() => {
        setCurrentPage(0);

        return searchText ? careRecipients.filter(it => {
            const matchByName = it.name.value.includes(searchText);
            const matchByHouseId = it.customerCode?.value.includes(searchText);
            return matchByName || matchByHouseId;
        }) : careRecipients;
    }, [careRecipients, searchText]);

    const displayList = useMemo(() => {
        const begin = currentPage * countPerPage;
        const end = begin + countPerPage;
        return searchedList.slice(begin, end);
    }, [currentPage, searchedList]);

    const handleViewReportClicked = useCallback((careRecipient: CareRecipient) => {
        dispatch(selectCareRecipientAction({ id: careRecipient.id, name: careRecipient.name, houseId: careRecipient.houseId, customerCode: careRecipient.customerCode, branchCode: careRecipient.branchCode }));
        navigate(`${Paths.dashBoard}/${careRecipient.id.value}`);
    }, [dispatch, history]);

    const handleConfigurationClicked = useCallback((event: MouseEvent, careRecipient: CareRecipient) => {
        event.stopPropagation();
        dispatch(selectCareRecipientAction({ id: careRecipient.id, name: careRecipient.name, houseId: careRecipient.houseId, customerCode: careRecipient.customerCode, branchCode: careRecipient.branchCode }));
        navigate(`${Paths.careRecipient}${Paths.config}/${careRecipient.id.value}`);
    }, [dispatch, history]);

    const value: ContextType = useMemo(() => ({
        countPerPage: countPerPage,
        currentPage: currentPage,
        setCurrentPage: setCurrentPage,
        searchText: searchText,
        setSearchText: setSearchText,
        searchedList: searchedList,
        displayList: displayList,
        handleViewReportClicked: handleViewReportClicked,
        handleConfigurationClicked: handleConfigurationClicked
    }), [currentPage, searchText, searchedList, displayList, handleViewReportClicked, handleConfigurationClicked]);

    return (
        <CareRecipientListContext.Provider value={value}>{props.children}</CareRecipientListContext.Provider>
    );
});

type Props = {
    children: ReactNode
};
